import Layout from 'components/layout';

import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { i18nFunct } from '@lucid/gatsby-theme-lucid';

import FlexiBoardMembers from 'components/flexi/flexi-board-members/flexi-board-members';
import FlexiCustomerLogos from 'components/flexi/flexi-customer-logos/flexi-customer-logos';
import FlexiImageSection from 'components/flexi/flexi-image-section/flexi-image-section';
import FlexiLeadership from 'components/flexi/flexi-leadership/flexi-leadership';
import FlexiLocationsSection from 'components/flexi/flexi-locations-section/flexi-locations-section';
import FlexiNewsroomPreviewSection from 'components/flexi/flexi-newsroom-preview-section/flexi-newsroom-preview-section';
import FlexiSocialProof from 'components/flexi/flexi-social-proof/flexi-social-proof';
import FlexiStyledMultiSections from 'components/flexi/flexi-styled-multi-sections/flexi-styled-multi-sections';
import FlexiStyledSection from 'components/flexi/flexi-styled-section/flexi-styled-section';
import FlexiTestimonials from 'components/flexi/flexi-testimonials/flexi-testimonials';
import FlexiFormattedSectionList from 'components/flexi/flexi-formatted-section-list/flexi-formatted-section-list';
import FlexiTimelineSection from 'components/flexi/flexi-timeline-section/flexi-timeline-section';
import FlexiJobs from 'components/flexi/flexi-jobs/flexi-jobs';
import FooterNew from 'components/footer/footer-new';
import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import NavbarNew from 'components/navbar/navbar-new';
import {
  Drupal_FieldNodeFlexipageFieldFlexiLinksButtons,
  Drupal_FieldNodeFlexipageFieldFlexiParagraphs,
  Drupal_NodeFlexipage,
  Drupal_NodeNews,
  Drupal_Paragraph,
  Drupal_ParagraphFlexiLinkButton
} from 'generated/graphql';
import { Text } from 'theme/typography';
import { LucidTheme, TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';
import { useMenuFooter, useMenuTopNavigation } from 'hooks/use-menu';

const Flexipage = ({ data, pageContext, location }: TemplateProps) => {
  const { langPrefix, pageType, nid, hideSsr, i18nKey } = pageContext;

  const { drupal, i18nData } = data;
  const i18n = i18nFunct(i18nData, i18nKey);
  const node = drupal.nodeById as Drupal_NodeFlexipage;
  const newsPreview = drupal.nodeQuery?.entities?.map(
    ({ entityTranslation }: any) => entityTranslation
  ) as Drupal_NodeNews[];
  const { entityMetatags, entityTranslations } = getEntityFields(node);

  const componentMapping: Record<string, any> = {
    flexi_board_members: FlexiBoardMembers,
    flexi_customer_logos: FlexiCustomerLogos,
    flexi_leaders: FlexiLeadership,
    flexi_locations_section: FlexiLocationsSection,
    flexi_newsroom_preview_section: FlexiNewsroomPreviewSection,
    flexi_social_proof: FlexiSocialProof,
    flexi_styled_multi_sections: FlexiStyledMultiSections,
    flexi_styled_section: FlexiStyledSection,
    flexi_testimonials: FlexiTestimonials,
    image_section: FlexiImageSection,
    flexi_formatted_section_list: FlexiFormattedSectionList,
    flexi_timeline_section: FlexiTimelineSection,
    flexi_jobs: FlexiJobs
  };

  const emphasizedHeader = !!node.fieldEmphasizeThePageHeader;
  const flexiParagraphs = node.fieldFlexiParagraphs as Drupal_FieldNodeFlexipageFieldFlexiParagraphs[];
  const flexiButtons = node.fieldFlexiLinksButtons as Drupal_FieldNodeFlexipageFieldFlexiLinksButtons[];

  const headingSizes = emphasizedHeader
    ? { default: 'headlineS700', tablet: 'headlineM700' }
    : { default: 'headlineXS700', tablet: 'headlineS700' };

  const newNavbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuFooter(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      <NavbarNew menu={newNavbarMenu} />
      <LucidContainer
        as="main"
        css={(theme: LucidTheme) => ({
          background: theme.colors.white,
          overflow: 'hidden'
        })}
        extraPadding={3}
      >
        <Box
          display="grid"
          gridColumnGap="20px"
          gridTemplateColumns={{
            default: 'repeat(4, 1fr)',
            tablet: 'repeat(12, 1fr)'
          }}
        >
          <Box
            gridColumn={{
              default: '1 / -1'
            }}
            maxWidth="780px"
            mb={4}
            mt={10}
            mx="auto"
          >
            {node.fieldFlexiPageSubheader && (
              <Heading
                as="h2"
                m={0}
                mb={3}
                textAlign="center"
                variant={{
                  default: 'subheadS700',
                  tablet: 'subheadM700'
                }}
              >
                {ReactHtmlParser(
                  getProcessedValue(node.fieldFlexiPageSubheader)
                )}
              </Heading>
            )}
            <Heading
              as="h1"
              css={(theme: LucidTheme) => ({
                '> u': {
                  display: 'inline-block',
                  position: 'relative',
                  textDecoration: 'none',
                  zIndex: theme.zIndices[1],
                  '&::before': {
                    content: '""',
                    background: theme.colors.coolGrey[10],
                    position: 'absolute',
                    height: '50%',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: theme.zIndices[0]
                  }
                }
              })}
              m={0}
              mb={6}
              textAlign="center"
              variant={headingSizes}
            >
              {ReactHtmlParser(getProcessedValue(node.fieldFlexiPageHeader))}
            </Heading>
            {node.fieldFlexiPageParagraph && (
              <Box
                css={{
                  textAlign: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  maxWidth: '580px'
                }}
              >
                <Text
                  as="div"
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.coolGrey[100],
                    marginBottom: theme.space[6]
                  })}
                  textStyles="introXS300"
                >
                  {ReactHtmlParser(
                    getProcessedValue(node.fieldFlexiPageParagraph)
                  )}
                </Text>
                <Box display="flex" justifyContent="center">
                  {(flexiButtons || []).map(({ entity }) => {
                    const {
                      fieldAnchorLink,
                      fieldLinkButtonLink,
                      fieldLinkButtonText,
                      fieldOpenInNewTab,
                      fieldUseButton,
                      fieldUsePrimary
                    } = entity as Drupal_ParagraphFlexiLinkButton;
                    let destination = fieldLinkButtonLink?.url?.path || '';
                    let onClick;
                    if (fieldAnchorLink) {
                      destination = `#${fieldAnchorLink}`;
                      onClick = () => {
                        // Just incase a malformed destination is passed in
                        try {
                          document.querySelector(destination)?.scrollIntoView();
                        } catch {
                          // Perhaps add logging here
                        }
                      };
                    }
                    return (
                      <Link
                        key={fieldLinkButtonText}
                        asButton={!!fieldUseButton}
                        css={(theme: LucidTheme) => ({
                          marginLeft: theme.space[2],
                          marginRight: theme.space[2]
                        })}
                        newTab={!!fieldOpenInNewTab}
                        onClick={onClick}
                        primary={!!fieldUsePrimary}
                        to={destination}
                      >
                        {fieldLinkButtonText}
                      </Link>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
          {flexiParagraphs.map(({ entity }) => {
            const { entityBundle, entityId } = entity as Drupal_Paragraph;
            const FlexiComponent = componentMapping[entityBundle as string];
            const newsroomPreviewSection = entityBundle === 'flexi_newsroom_preview_section' ? newsPreview.filter(Boolean) : [];
            if (entityBundle === 'flexi_leaders' && FlexiComponent) {
              return (
                <FlexiComponent
                  key={entityId}
                  entity={entity}
                  showLess={i18n('mktg_show_less')}
                  showMore={i18n('mktg_show_more')}
                />
              );
            } else {
              return FlexiComponent ? (
                <FlexiComponent
                  key={entityId}
                  entity={entity}
                  newsPreview={newsroomPreviewSection}
                />
              ) : null;
            }
          })}
        </Box>
      </LucidContainer>
      {node.fieldFlexiPrefooterHeading && node.fieldFlexiPrefooterLink && (
        <Box
          css={(theme: LucidTheme) => ({
            backgroundColor: theme.colors.blueSteel,
            textAlign: 'center',
            padding: theme.space[10]
          })}
        >
          <Heading
            as="h2"
            css={(theme: LucidTheme) => ({
              color: theme.colors.white,
              marginTop: 0,
              marginBottom: theme.space[7]
            })}
            variant="subheadS700"
          >
            {node.fieldFlexiPrefooterHeading}
          </Heading>
          <Link asButton to={node.fieldFlexiPrefooterLink?.url?.path || ''}>
            {node.fieldFlexiPrefooterLink.title}
          </Link>
        </Box>
      )}
      <FooterNew entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default Flexipage;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_show_more
            mktg_show_less
          }
        }
      }
    }
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeFlexipage {
          ...entityFields
          fieldEmphasizeThePageHeader
          fieldFlexiPageHeader {
            processed
          }
          fieldFlexiPageSubheader {
            processed
          }
          fieldFlexiPageParagraph {
            processed
          }
          fieldFlexiLinksButtons {
            entity {
              ... on Drupal_ParagraphFlexiLinkButton {
                fieldAnchorLink
                fieldLinkButtonLink {
                  url {
                    path
                  }
                  title
                }
                fieldLinkButtonText
                fieldOpenInNewTab
                fieldUseButton
                fieldUsePrimary
              }
            }
          }
          fieldFlexiPrefooterHeading
          fieldFlexiPrefooterLink {
            url {
              path
            }
            title
          }
          fieldFlexiParagraphs {
            entity {
              entityId
              entityBundle
              ...flexiBoardMembers
              ...flexiCustomerLogosSection
              ...flexiImageSection
              ...flexiLeadership
              ...flexiLocationsSection
              ...flexiNewsroomPreviewSection
              ...flexiSocialProof
              ...flexiStyledMultiSections
              ...flexiStyledSection
              ...flexiTestimonialSection
              ...flexiFormattedSectionList
              ...flexiTimelineSection
              ...flexiJobs
            }
          }
        }
      }
      nodeQuery(
        sort: [{ field: "field_date.value", direction: DESC }]
        limit: 3
        filter: {
          conditions: [
            { field: "type", value: "news", operator: EQUAL }
            { field: "status", value: "1", operator: EQUAL }
          ]
        }
      ) {
        entities {
          ... on Drupal_Node {
            entityTranslation(language: $LanguageId) {
              ... on Drupal_NodeNews {
                nid
                title
                fieldDate {
                  value
                }
                fieldImageLink
                fieldPrProduct
                fieldPublisher
                fieldSourceUrl
              }
            }
          }
        }
      }
    }
  }
`;
